import { t } from '@lingui/core/macro';
import gql from 'graphql-tag';
import { useMemo } from 'react';

import { type AudienceOptionType } from '@/api/audience';
import { getVariablesFromTableParamsNew } from '@/api/util/getVariablesFromTableParams';
import { InstantSearchSortOrder, type SolQueryParamsNew } from '@/components/InstantSearch';
import { useSolImmutableQuery, useSolQuery } from '@/hooks/useSolQuery';
import { ROUTES } from '@/router';
import { COLOR_GRAY_500 } from '@/styles/palette';

import {
  type DirectVendorViewThroughGQLResponse,
  type OpportunityVendorActivityGQLResponse,
  type VendorActivityGQLResponse,
  type VendorActivityKPIsGQLResponse,
  type VendorListGQLResponse,
  type VendorVisitTrendGQLResponse,
  type ViewThroughValueDataType,
} from './vendorType';

export const useVendorList = ({ channelId }: { channelId?: string } = {}) => {
  const { data, error, isLoading } = useSolImmutableQuery<VendorListGQLResponse>({
    query: gql`
      query GetVendorList(
        $page: Pagination!
        $sort: [SortParamInput]!
        $filter: [FilterParamInput]!
      ) {
        vendors {
          get(page: $page, sort: $sort, filter: $filter) {
            edges {
              node {
                id
                name
                color
                channel {
                  id
                  name
                  color
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      ...getVariablesFromTableParamsNew({
        page: {
          offset: 0,
          limit: 100,
        },
        sort: {
          field: 'name',
          direction: InstantSearchSortOrder.ASC,
        },
        filter: [
          ...(channelId
            ? [{ field: 'channelId', operator: 'eq' as const, operand: channelId }]
            : []),
        ],
      }),
    },
  });

  return {
    vendors: data?.vendors.get.edges.map((edge) => edge.node),
    error,
    isLoading,
  };
};

export const useVendorById = (vendorId?: string) => {
  const { vendors, isLoading, error } = useVendorList();
  const vendor = vendors?.find((item) => item.id === vendorId);

  return {
    vendor,
    error,
    isLoading,
  };
};

export const useVendorActivityKPIs = (
  startDate?: string,
  endDate?: string,
  audience?: AudienceOptionType,
) => {
  const canExecuteQuery = startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<VendorActivityKPIsGQLResponse>({
    query:
      !!canExecuteQuery &&
      gql`
        query VendorTrafficKPIs(
          $startDate: DateTime!
          $endDate: DateTime!
          $audienceIds: [String]
        ) {
          visualization {
            trafficKpi2(startDate: $startDate, endDate: $endDate, audienceIds: $audienceIds) {
              audienceVisits
              audienceVisitsLastPeriod
              audienceVisitsPercentChange
              closedWonInfluenced
              closedWonInfluencedLastPeriod
              closedWonInfluencedPercentChange
              engagedCompanies
              engagedCompaniesLastPeriod
              engagedCompaniesPercentChange
              pipelineInfluenced
              pipelineInfluencedLastPeriod
              pipelineInfluencedPercentChange
              tamVisits
              tamVisitsLastPeriod
              tamVisitsPercentChange
              visits
              visitsLastPeriod
              visitsPercentChange
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      audienceIds: audience?.id ? [audience.id] : undefined,
    },
  });

  const vendorActivityKpis = useMemo(() => {
    const {
      visits,
      visitsLastPeriod,
      visitsPercentChange,
      tamVisits,
      tamVisitsLastPeriod,
      tamVisitsPercentChange,
      audienceVisits,
      audienceVisitsLastPeriod,
      audienceVisitsPercentChange,
      engagedCompanies,
      engagedCompaniesLastPeriod,
      engagedCompaniesPercentChange,
      pipelineInfluenced,
      pipelineInfluencedLastPeriod,
      pipelineInfluencedPercentChange,
      closedWonInfluenced,
      closedWonInfluencedLastPeriod,
      closedWonInfluencedPercentChange,
    } = data?.visualization.trafficKpi2 ?? {};

    return [
      {
        id: 'visits',
        label: t`Total Visits`,
        value: visits,
        lastPeriod: visitsLastPeriod,
        percentChange: visitsPercentChange,
        valueFormat: {},
      },
      {
        id: 'targetVisits',
        label: audience?.isTAM
          ? t`TAM Visits`
          : audience?.name
            ? t`${audience.name} Visits`
            : t`Audience Visits`,
        value: audience?.id ? audienceVisits : tamVisits,
        lastPeriod: audience?.id ? audienceVisitsLastPeriod : tamVisitsLastPeriod,
        percentChange: audience?.id ? audienceVisitsPercentChange : tamVisitsPercentChange,
        valueFormat: {},
      },
      {
        id: 'engagedCompanies',
        label: audience?.isTAM
          ? t`TAM Engaged Companies`
          : audience?.name
            ? t`${audience.name} Engaged Companies`
            : t`Audience Engaged Companies`,
        value: engagedCompanies,
        valueLink:
          engagedCompanies != null &&
          engagedCompanies > 0 &&
          ROUTES.webTrafficActivityWithParams({
            'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
            'w.inTam': audience?.isTAM ? undefined : 'true',
            'w.metrics.visits': '[1,]',
          }),
        lastPeriod: engagedCompaniesLastPeriod,
        percentChange: engagedCompaniesPercentChange,
        valueFormat: {},
      },
      {
        id: 'pipeline',
        label: t`Pipeline Influence`,
        value: pipelineInfluenced,
        lastPeriod: pipelineInfluencedLastPeriod,
        percentChange: pipelineInfluencedPercentChange,
        valueFormat: { isCurrency: true, precision: 0 },
      },
      {
        id: 'closedWon',
        label: t`Closed Won Influence`,
        value: closedWonInfluenced,
        lastPeriod: closedWonInfluencedLastPeriod,
        percentChange: closedWonInfluencedPercentChange,
        valueFormat: { isCurrency: true, precision: 0 },
      },
    ];
  }, [data, audience]);

  return {
    vendorActivityKpis,
    isLoading,
    error,
  };
};

export const useVendorActivity = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
  audienceId?: string,
  opportunityId?: string,
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<VendorActivityGQLResponse>(
    {
      query:
        !!canExecuteQuery &&
        gql`
          query VendorActivity2(
            $page: Pagination!
            $sort: [SortParamInput]!
            $filter: [FilterParamInput]!
            $startDate: DateTime!
            $endDate: DateTime!
            $audienceId: String
            $opportunityId: String
          ) {
            visualization {
              vendorActivity2(
                page: $page
                sort: $sort
                filter: $filter
                startDate: $startDate
                endDate: $endDate
                audienceId: $audienceId
                opportunityId: $opportunityId
              ) {
                edges {
                  node {
                    adClicks
                    adImpressions
                    allVisits: targetVisits
                    allVisitsBest: visitsBest
                    allVisitsWorst: visitsWorst
                    baseAccounts
                    botVisits
                    closedWonFirstTouchAttribution
                    closedWonInfluenced
                    closedWonLinearAttribution
                    companiesReached
                    countCampaigns
                    countClosedwonInfluenced
                    countPipelineInfluenced
                    engagedCompanies
                    engagedCompaniesBest
                    engagedCompaniesWorst
                    fitScore
                    fitScoreBest
                    fitScoreWorst
                    leadsInfluenced
                    otherVisits
                    opportunitiesInfluenced
                    opportunityInfluencedBest
                    opportunityInfluencedWorst
                    percentOfEngagedCompanies
                    percentOpportunitiesInfluenced
                    pipelineFirstTouchAttribution
                    pipelineInfluenced
                    pipelineInfluencedBest
                    pipelineInfluencedPerSpend
                    pipelineInfluencedWorst
                    pipelineLastTouchAttribution
                    pipelineLinearAttribution
                    spend
                    spendPerTargetVisit
                    spendPerVisitBest
                    spendPerVisitWorst
                    botViewThroughVisits
                    otherViewThroughVisits
                    targetViewThroughVisits
                    targetVisits
                    unresolvedViewThroughVisits
                    unresolvedVisits
                    vendor {
                      channel {
                        id
                        name
                      }
                      id
                      name
                    }
                    viewThroughPercent
                    viewThroughVisits
                    visitEfficiency
                  }
                }
                totalEdges
              }
            }
          }
        `,
      variables: {
        ...getVariablesFromTableParamsNew(tableParams),
        page: {
          offset: tableParams?.page.offset ?? 0,
          limit: tableParams?.page.limit ?? 100,
        },
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId,
        opportunityId,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    vendorActivity: data?.visualization.vendorActivity2.edges.map((edge) => edge.node),
    totalResults: data?.visualization.vendorActivity2.totalEdges,
    isLoading,
    error,
  };
};

export const useVendorOpportunityActivity = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
  audienceId?: string,
  opportunityId?: string,
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;

  const { data, isLoading, error } = useSolQuery<OpportunityVendorActivityGQLResponse>(
    {
      query:
        !!canExecuteQuery &&
        gql`
          query OpportunityVendorActivity(
            $page: Pagination!
            $sort: [SortParamInput]!
            $filter: [FilterParamInput]!
            $startDate: DateTime!
            $endDate: DateTime!
            $opportunityId: String
          ) {
            visualization {
              opportunityVendorActivity(
                page: $page
                sort: $sort
                filter: $filter
                startDate: $startDate
                endDate: $endDate
                opportunityId: $opportunityId
              ) {
                edges {
                  node {
                    audienceVisits
                    closedWonLinearAttribution
                    closedWonInfluenced
                    closedWonFirstTouchAttribution
                    pipelineFirstTouchAttribution
                    pipelineInfluenced
                    pipelineLastTouchAttribution
                    pipelineLinearAttribution
                    vendor {
                      color
                      id
                      name
                      channel {
                        color
                        id
                        name
                      }
                    }
                  }
                }
                totalEdges
              }
            }
          }
        `,
      variables: {
        ...getVariablesFromTableParamsNew(tableParams),
        page: {
          offset: tableParams?.page.offset ?? 0,
          limit: tableParams?.page.limit ?? 100,
        },
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId,
        opportunityId,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    vendorActivity: data?.visualization.opportunityVendorActivity.edges.map((edge) => edge.node),
    totalResults: data?.visualization.opportunityVendorActivity.totalEdges,
    isLoading,
    error,
  };
};

export const useVendorVisitTrend = (
  vendorId?: string,
  startDate?: string,
  endDate?: string,
  audienceId?: string,
) => {
  const canExecuteQuery = vendorId && startDate?.length && endDate?.length;

  const { data, isLoading, error } = useSolQuery<VendorVisitTrendGQLResponse>({
    query:
      !!canExecuteQuery &&
      gql`
        query TrafficVisitTrend3(
          $startDate: DateTime!
          $endDate: DateTime!
          $audienceIds: [String]
          $vendorIds: [String]
        ) {
          visualization {
            trafficVisitTrend3(
              startDate: $startDate
              endDate: $endDate
              audienceIds: $audienceIds
              vendorIds: $vendorIds
            ) {
              botVisits: bot
              otherVisits: other
              sundayOfWeek
              targetVisits: target
              totalVisits: total
              unresolvedVisits: unresolved
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      audienceIds: audienceId ? [audienceId] : undefined,
      vendorIds: vendorId ? [vendorId] : undefined,
    },
  });

  return {
    vendorVisitTrend: data?.visualization.trafficVisitTrend3,
    isLoading,
    error,
  };
};

export const useROMSByVendorId = (
  vendorId?: string,
  startDate?: string,
  endDate?: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  audienceId?: string,
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canExecuteQuery = !!(vendorId && startDate?.length && endDate?.length);

  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    romsData: [] as any,
    isLoading: false,
    error: undefined,
  };
};

export const useDirectVendorViewThrough = (
  startDate?: string,
  endDate?: string,
  audienceId?: string,
) => {
  const canExecuteQuery = !!(startDate?.length && endDate?.length);

  const { data, isLoading, error } = useSolQuery<DirectVendorViewThroughGQLResponse>(
    {
      query:
        !!canExecuteQuery &&
        gql`
          query TrafficDirectAllocation(
            $startDate: DateTime!
            $endDate: DateTime!
            $audienceId: String
          ) {
            visualization {
              trafficDirectAllocation(
                startDate: $startDate
                endDate: $endDate
                audienceId: $audienceId
              ) {
                channels {
                  color
                  id
                  name
                }
                vendors {
                  color
                  id
                  name
                  channel {
                    id
                  }
                }
                values {
                  id
                  value
                }
              }
            }
          }
        `,
      variables: {
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  const viewThroughData = useMemo(() => {
    if (!data?.visualization.trafficDirectAllocation) {
      return undefined;
    }

    const { channels, vendors, values } = data.visualization.trafficDirectAllocation;

    const channelsNoDirect = channels.filter((channel) => channel.id !== 'chnl_direct');
    const vendorsNoDirect = vendors.filter((vendor) => vendor.id !== 'vndr_direct');
    const valuesNoDirect = values.filter(
      (val) => val.id !== 'vndr_direct' && val.id !== 'chnl_direct',
    );

    const links = valuesNoDirect.map<ViewThroughValueDataType>((val) => {
      let sourceId;
      let targetId;

      if (val.id.startsWith('vndr_')) {
        const source = vendorsNoDirect.find((vendor) => vendor.id === val.id);
        sourceId = source?.channel?.id;
        targetId = source?.id;
      } else {
        const target = channelsNoDirect.find((channel) => channel.id === val.id);
        sourceId = 'root';
        targetId = target?.id;
      }

      if (!sourceId || !targetId) {
        throw new Error('Invalid data');
      }

      return {
        sourceId,
        targetId,
        value: val.value,
      };
    });

    if (channelsNoDirect.length === 0 && vendorsNoDirect.length === 0) {
      return undefined;
    }

    const nodes = [
      { id: 'root', name: t`Direct`, color: COLOR_GRAY_500, level: 1 },
      ...channelsNoDirect.map((channel) => ({
        id: channel.id,
        name: channel.name,
        color: channel.id === 'chnl_direct' ? COLOR_GRAY_500 : channel.color!,
        level: 2,
      })),
      ...vendorsNoDirect.map((vendor) => ({
        id: vendor.id,
        name: vendor.name,
        color: vendor.color!,
        level: 3,
      })),
    ];

    return { nodes, links };
  }, [data]);

  return {
    viewThroughData,
    isOnlyDirectTraffic: viewThroughData?.links?.length === 1,
    isLoading,
    error,
  };
};
